import { useCallback, useEffect, useRef, useState } from "react";

import { inspectorAuthInteractor } from "@/client/amplify/auth";
import { factoryIdStorage } from "@/utils/storageLocal";

const logInfo = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== "test") {
    console.log(...args);
  }
};

/**
 * CloudFront Signed Cookieを管理するカスタムフック
 *
 */
export const useSignedCookies = () => {
  const refreshThresholdMinutes = 5;
  // 60秒ごとにCookie有効期限をチェック
  const checkIntervalMs = 60000;

  const [isReady, setIsReady] = useState<boolean>(false);
  const [authToken, setAuthToken] = useState<string>("");
  const cookieInfoRef = useRef<{ expires: string | null } | null>(null);

  const updateCookieInfoIfChanged = useCallback((next: { expires: string | null } | null) => {
    const prev = cookieInfoRef.current;
    const changed =
      (prev === null && next !== null) ||
      (prev !== null && next === null) ||
      (prev !== null && next !== null && prev.expires !== next.expires);

    cookieInfoRef.current = next;
    if (changed) {
      const nextReady = next?.expires !== null && next?.expires !== undefined;
      setIsReady((prevReady) => (prevReady !== nextReady ? nextReady : prevReady));
    }
  }, []);

  /**
   * Cookieを解析してCookieInfo形式に変換する
   */
  const parseCookies = useCallback(() => {
    const cookies = document.cookie.split(";").reduce(
      (acc, cookie) => {
        const [key, value] = cookie.trim().split("=");
        if (key) acc[key] = value ?? "";
        return acc;
      },
      {} as Record<string, string>,
    );

    return {
      expires: cookies.cf_expires || null,
    };
  }, []);

  /**
   * Cookieを取得する（初回）
   */
  const signInCookies = useCallback(async () => {
    if (!authToken) return;

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      const factoryId = await factoryIdStorage.read();
      let isMasterUser = false;
      try {
        const authInfo = await inspectorAuthInteractor.getAuthInfo({});
        isMasterUser = authInfo?.isMasterUser ?? false;
      } catch (e) {
        console.warn("Auth info refresh failed, proceeding without assumed header", e);
      }
      if (isMasterUser && factoryId) {
        headers["X-Assumed-Steel-Mill-Id"] = factoryId;
      }

      const response = await fetch("/api/auth/signin", {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        // 少し待ってからCookie情報を取得
        setTimeout(() => {
          const newCookieInfo = parseCookies();
          updateCookieInfoIfChanged(newCookieInfo);
        }, 100);
      } else {
        console.error("Cookie signin failed:", response.status);
      }
    } catch (error) {
      console.error("Cookie signin error:", error);
    }
  }, [authToken, parseCookies, updateCookieInfoIfChanged]);

  /**
   * Cookieを更新する
   */
  const refreshCookies = useCallback(async () => {
    if (!authToken) return;

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      const factoryId = await factoryIdStorage.read();
      let isMasterUser = false;
      try {
        const authInfo = await inspectorAuthInteractor.getAuthInfo({});
        isMasterUser = authInfo?.isMasterUser ?? false;
      } catch (e) {
        console.warn("Auth info refresh failed, proceeding without assumed header", e);
      }
      if (isMasterUser && factoryId) {
        headers["X-Assumed-Steel-Mill-Id"] = factoryId;
      }
      const response = await fetch("/api/auth/refresh-cookies", {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        // 少し待ってからCookie情報を取得
        setTimeout(() => {
          const newCookieInfo = parseCookies();
          updateCookieInfoIfChanged(newCookieInfo);
        }, 100);
      } else {
        console.error("Cookie refresh failed:", response.status);
      }
    } catch (error) {
      console.error("Cookie refresh error:", error);
    }
  }, [authToken, parseCookies, updateCookieInfoIfChanged]);

  /**
   * Cookie有効期限をチェックし、必要に応じて更新する
   */
  const checkAndRefreshIfNeeded = useCallback(() => {
    const cookies = parseCookies();
    updateCookieInfoIfChanged(cookies); // 値が変わったときのみ再レンダリング
    const cfExpires = cookies.expires;

    const expiresTimestamp = cfExpires ? Number.parseInt(cfExpires, 10) : Number.NaN;
    const now = Math.floor(Date.now() / 1000);
    const remainingMinutes = Math.floor((expiresTimestamp - now) / 60);

    if (Number.isNaN(expiresTimestamp) || remainingMinutes <= 0) {
      // 期限切れまたはCookieが存在しない場合は初回取得を試みる
      signInCookies();
      return;
    }

    if (remainingMinutes <= refreshThresholdMinutes) {
      logInfo(`Cookie expiring soon (${remainingMinutes} minutes remaining), refreshing...`);
      refreshCookies();
    }
  }, [parseCookies, signInCookies, refreshCookies, updateCookieInfoIfChanged]);

  // 初期化: 認証状態を確認し、authTokenとsteelMillIdを取得
  useEffect(() => {
    const initialize = async () => {
      try {
        const signedIn = await inspectorAuthInteractor.isSignIn();
        if (signedIn) {
          const token = await inspectorAuthInteractor.getAuthToken();
          setAuthToken(token);
        }
      } catch (error) {
        console.error("Authentication check error:", error);
      }
    };
    initialize();
  }, []);

  // authTokenが取得できたら、既存のCookieを読み込み、必要に応じて取得/更新
  useEffect(() => {
    if (!authToken) return;

    checkAndRefreshIfNeeded();
  }, [authToken, checkAndRefreshIfNeeded]);

  // 定期的にCookie有効期限をチェック
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkAndRefreshIfNeeded();
    }, checkIntervalMs);

    return () => clearInterval(intervalId);
  }, [checkAndRefreshIfNeeded]);

  return {
    isReady,
  };
};
